import TwoHearts from "@/pages/TwoHearts";
import TwoHeartsDuel from "@/pages/TwoHeartsDuel";
import CandySaga from "@/pages/CandySaga";
import HeartRunner from "@/pages/HeartRunner";
import HeartRunnerDuel from "@/pages/HeartRunnerDuel";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { AssistantBubble } from "@/components/AssistantBubble";
import { LogoIntro } from "@/components/LogoIntro";
import ProtectedRoute from "@/components/ProtectedRoute";
import WhatsAppCommunity from "@/pages/WhatsAppCommunity";
import Landing from "@/pages/Landing";
import { LoginPage, RegisterPage, ForgotPage, ResetPage } from "@/pages/Auth";
import Leaderboard from "@/pages/Leaderboard";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import Community from "@/pages/Community";
import PublicProfile from "@/pages/PublicProfile";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Rewards from "@/pages/Rewards";
import Invite from "@/pages/Invite";
import PushGuide from "@/pages/PushGuide";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import MemberExclusions from "@/pages/MemberExclusions";
import Feed from "@/pages/Feed";
import { detectAdSource } from "@/lib/adSource";

detectAdSource();
import MenuPage from "@/pages/MenuPage";
import SettingsPage from "@/pages/SettingsPage";
import Mood from "@/pages/Mood";
import Lotto from "@/pages/Lotto";
import JackpotRoulette from "@/pages/JackpotRoulette";
import Pet from "@/pages/Pet";
import Teams from "@/pages/Teams";
import Playground from "@/pages/Playground";
import LegalPage from "@/pages/Legal";
import AppPage from "@/pages/AppPage";
import HeartChain from "@/pages/HeartChain";
import HonorWall from "@/pages/HonorWall";
import Team from "@/pages/Team";
import Halloween from "@/pages/Halloween";
import MugThanks from "@/pages/MugThanks";
import MugPresentation from "@/pages/MugPresentation";
import ThanksStefan from "@/pages/ThanksStefan";
import { HalloweenLayer } from "@/components/HalloweenLayer";
import Soulmate from "@/pages/Soulmate";
import Memorial from "@/pages/Memorial";
import Supporters from "@/pages/Supporters";
import HeartLink from "@/pages/HeartLink";
import BioLinks, { PublicSupport } from "@/pages/BioLinks";
import HallOfFame from "@/pages/HallOfFame";
import { GameGate } from "@/components/GameGate";
import VaultDuel from "@/pages/VaultDuel";
import { GameMusicController } from "@/components/games/GameMusic";
import { GameArena } from "@/components/games/GameArena";
import Sky from "@/pages/Sky";
import PointsGuide from "@/pages/PointsGuide";
import Quest from "@/pages/Quest";
import VerifyEmail from "@/pages/VerifyEmail";
import Messages, { Thread } from "@/pages/Messages";
import Notifications, { PostPage } from "@/pages/Notifications";
import CommunitySupport from "@/pages/CommunitySupport";

function SupportRedirect() {
  const { hash } = useLocation();
  return <Navigate to={`/community-unterstuetzen${hash}`} replace />;
}

import { ShareSheetHost } from "@/components/growth/ShareSheet";
import { InviteGateHost } from "@/components/growth/InviteLock";
import { captureRefParams } from "@/lib/share";

captureRefParams();

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrieren" element={<RegisterPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPage />} />
      <Route path="/passwort-zuruecksetzen" element={<ResetPage />} />
      <Route path="/links" element={<BioLinks />} />
      <Route path="/projekt-unterstuetzen" element={<PublicSupport />} />
      <Route path="/video" element={<Navigate to="/" replace />} />
      <Route path="/ruhmeshalle" element={<HallOfFame />} />
      <Route path="/email-bestaetigen" element={<VerifyEmail />} />
      <Route path="/video/:id" element={<Navigate to="/" replace />} />
      <Route path="/impressum" element={<LegalPage key="impressum" page="impressum" />} />
      <Route path="/datenschutz" element={<LegalPage key="datenschutz" page="datenschutz" />} />
      <Route path="/agb" element={<LegalPage key="agb" page="agb" />} />
      <Route path="/nutzungsbedingungen" element={<LegalPage key="nutzungsbedingungen" page="nutzungsbedingungen" />} />
      <Route path="/teilnahmebedingungen" element={<LegalPage key="teilnahmebedingungen" page="teilnahmebedingungen" />} />
      <Route path="/verlosung/*" element={<Navigate to="/feed" replace />} />
      <Route path="/app" element={<AppPage />} />
      <Route path="/herz/:token" element={<HeartLink />} />
      <Route path="/zwei-herzen" element={<TwoHearts />} />
      <Route path="/zwei-herzen/duell/:id" element={<TwoHeartsDuel />} />
      <Route path="/herz-laeufer" element={<HeartRunner />} />
      <Route path="/zuckerherz" element={<CandySaga />} />
      <Route path="/herz-laeufer/duell/:id" element={<HeartRunnerDuel />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/feed" element={<Feed />} />
        <Route path="/mitteilungen" element={<Notifications />} />
        <Route path="/nachrichten" element={<Messages />} />
        <Route path="/nachrichten/:userId" element={<Thread />} />
        <Route path="/post/:postId" element={<PostPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/videos" element={<Navigate to="/feed" replace />} />
        <Route path="/geschenke" element={<Rewards />} />
        <Route path="/einladen" element={<Invite />} />
        <Route path="/sternenhimmel" element={<Sky />} />
        <Route path="/punkte-sammeln" element={<PointsGuide />} />
        <Route path="/vibes" element={<Navigate to="/feed" replace />} />
        <Route path="/community-aufgabe" element={<Quest />} />
        <Route path="/herz-kette" element={<HeartChain />} />
        <Route path="/ehrenwand" element={<HonorWall />} />
        <Route path="/team" element={<Team />} />
        <Route path="/halloween" element={<Halloween />} />
        <Route path="/dankeschoen-tasse" element={<MugThanks />} />
        <Route path="/tasse" element={<MugPresentation />} />
        <Route path="/danke-stefan" element={<ThanksStefan />} />
        <Route path="/shop" element={<Navigate to="/dankeschoen-tasse" replace />} />
        <Route path="/seelenverwandter" element={<Navigate to="/heimlicher-chat" replace />} />
        <Route path="/heimlicher-chat" element={<Soulmate />} />
        <Route path="/gedenken" element={<Memorial />} />
        <Route path="/unterstuetzer" element={<Supporters />} />
        <Route path="/rangliste" element={<Leaderboard />} />
        <Route path="/spiele" element={<Games />} />
        <Route path="/gemeinsam-punkte-sammeln" element={<WhatsAppCommunity />} />
        <Route path="/spiele/tresor-duell/:id" element={<GameGate game="vault_duel"><GameArena><VaultDuel /></GameArena></GameGate>} />
        <Route path="/spiele/:slug" element={<GameDetail />} />
        <Route path="/community" element={<Community />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/push" element={<PushGuide />} />
        <Route path="/profil" element={<Profile />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route path="/unterstuetzen" element={<SupportRedirect />} />
        <Route path="/community-unterstuetzen" element={<CommunitySupport />} />
        <Route path="/stimmung" element={<GameGate game="mood"><Mood /></GameGate>} />
        <Route path="/jackpot-roulette" element={<JackpotRoulette />} />
        <Route path="/lotto" element={<GameGate game="lotto"><GameArena><Lotto /></GameArena></GameGate>} />
        <Route path="/haustier" element={<GameGate game="pet"><Pet /></GameGate>} />
        <Route path="/teams" element={<GameGate game="teams"><Teams /></GameGate>} />
        <Route path="/spielplatz" element={<GameGate game="pet"><Playground /></GameGate>} />
        <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
        <Route path="/admin/mitglied/:id" element={<ProtectedRoute admin><MemberExclusions /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <GameMusicController />
          <AssistantBubble />
          <ShareSheetHost />
          <InviteGateHost />
          <Toaster position="top-center" richColors />
          <LogoIntro />
          <HalloweenLayer />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
